import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  type PharmacyDocumentSummary,
  type SellerLicence,
  type SellerPharmacy,
} from '@chicare/shared';

import {
  useSellerPharmacy,
  useSubmitLicenceRenewal,
  useSubmitPharmacy,
  useUpdatePharmacy,
  useUploadPharmacyDocument,
} from '@/lib/onboardingQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from '@/features/admin/AdminError';

/**
 * Finishing your registration.
 *
 * ── THE CHECKLIST IS THE SCREEN ─────────────────────────────────────────────
 *
 * Everything here answers one question: what is still standing between me and
 * selling? The API returns that as human sentences rather than field names, and
 * the screen shows the list shrinking as it is filled in. A pharmacist should
 * never have to guess which of eleven fields is the problem.
 *
 * ── FOUR STATES, FOUR DIFFERENT SCREENS ─────────────────────────────────────
 *
 *   draft             the checklist, editable
 *   pending_approval  frozen, with an honest wait time
 *   rejected          the checklist again, with what was wrong at the top
 *   active            done — and a way through to actually selling
 *
 * The rejected state matters most. A written reason a pharmacy cannot act on is
 * just an insult, so it is the first thing on the page and editing reopens.
 */
export function OnboardingScreen() {
  const { data, isLoading, isError, error, refetch } = useSellerPharmacy();

  if (isError) {
    return (
      <AdminError title="We couldn't load your registration" error={error} onRetry={() => refetch()} />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[720px] space-y-4 px-4 py-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40 w-full" radius="card" />
      </div>
    );
  }

  if (!data.meta.registered || !data.pharmacy) {
    return (
      <div className="mx-auto w-full max-w-[560px] px-4 py-10 text-center">
        {/* Deliberately not "pharmacy": there is no record here, so there is
            no sector to read, and guessing wrong would greet a vet practice
            with a word for something it is not. */}
        <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
          Nothing registered on this login
        </h1>
        <p className="mx-auto mt-2 max-w-[40ch] text-[13.5px] leading-relaxed text-muted">
          This account is not attached to a pharmacy or a veterinary business. If you registered
          with a different email, sign in with that one.
        </p>
      </div>
    );
  }

  const { pharmacy, meta } = data;

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-5 xl:px-8">
      <header className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">
            {pharmacy.name}
          </h1>
          {/* The licences, not the status column. Selling stops the instant a
              licence lapses, while the status only catches up on the nightly
              run — so for those hours the badge would have read LIVE above a
              card explaining that nothing was on sale. */}
          <StatusBadge
            status={pharmacy.status}
            paused={(meta.licences ?? []).some((licence) => licence.expired)}
          />
        </div>
        <p className="mt-1 text-[13px] text-muted">{pharmacy.premises_address}</p>
        <SectorSummary pharmacy={pharmacy} />
      </header>

      {/* ── Licences ─────────────────────────────────────────────────────
          Rendered in every state, including `active`, which is the whole
          point. An expired licence takes a seller's entire shelf off sale,
          and until now the console hid the documents panel from live
          pharmacies — so the one screen that could have taken the renewal
          was the one screen they could not use. */}
      {(meta.licences ?? []).map((licence) => (
        <LicencePanel key={licence.sector} licence={licence} multiple={(meta.licences ?? []).length > 1} />
      ))}

      {pharmacy.status === 'pending_approval' ? <UnderReview /> : null}
      {pharmacy.status === 'rejected' ? <Rejected documents={pharmacy.documents} /> : null}
      {pharmacy.status === 'active' || pharmacy.paused_for_licence ? (
        <Live licences={meta.licences ?? []} />
      ) : null}

      {pharmacy.status !== 'active' ? (
        <>
          <Checklist missing={meta.missing ?? []} ready={meta.ready_to_submit ?? false} />
          <Documents
            pharmacy={pharmacy}
            required={meta.required_documents}
            editable={meta.editable ?? false}
          />
          <Details pharmacy={pharmacy} editable={meta.editable ?? false} />
          {meta.editable ? <SubmitPanel ready={meta.ready_to_submit ?? false} /> : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Which shop or shops this seller is in.
 *
 * Directly under the name, because everything else on the screen follows from
 * it: the checklist, the documents, the licence fields and eventually which
 * catalogue they can list from. A pharmacist who reads "Animal health" here and
 * did not mean it has found the mistake in two seconds rather than after
 * uploading four documents.
 */
function SectorSummary({ pharmacy }: { pharmacy: SellerPharmacy }) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      {pharmacy.sectors.map((sector) => (
        <span
          key={sector}
          data-testid={`sector-chip-${sector}`}
          className={cn(
            'rounded-pill border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em]',
            sector === 'veterinary' ? 'border-blue text-blue' : 'border-blue text-blue',
          )}
        >
          {sector === 'veterinary' ? 'Animal health' : 'Human medicines'}
        </span>
      ))}
    </div>
  );
}

/**
 * One licence, and whatever needs doing about it.
 *
 * Four states, and the screen is genuinely different in each:
 *
 *   fine       nothing. A panel that shouts about a licence expiring in eight
 *              months trains people to ignore the one that matters.
 *   due soon   the form, opened, with the days remaining said plainly
 *   expired    what has stopped, then the form
 *   with us    no form — an honest wait, and the date they sent
 *
 * A rejection is shown above the form rather than beside it, because it is the
 * thing they have to read before they upload the same photograph again.
 */
function LicencePanel({ licence, multiple }: { licence: SellerLicence; multiple: boolean }) {
  const submit = useSubmitLicenceRenewal();
  const input = useRef<HTMLInputElement>(null);

  const [licenceNo, setLicenceNo] = useState(licence.licence_no ?? '');
  const [expiry, setExpiry] = useState('');
  const [file, setFile] = useState<File | null>(null);

  // Nothing to say while a licence is comfortably in date.
  if (!licence.renewal_due && !licence.pending_review && !licence.last_rejection) {
    return null;
  }

  const errors = submit.error instanceof ApiError ? submit.error.fieldErrors : {};
  const ready = licenceNo.trim() !== '' && expiry !== '' && file !== null;

  if (licence.pending_review) {
    return (
      <Card className="mb-4" data-testid={`licence-pending-${licence.sector}`}>
        <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
          {licence.regulator} · with our team
        </p>
        <h2 className="m-0 mt-1 font-display text-[15px] font-bold tracking-[-0.01em]">
          Your {licence.label} is being checked
        </h2>
        <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
          We read licences by hand, so this usually takes a working day.
          {licence.expired
            ? ` ${licence.affects} come back on their own the moment it is approved — you do not need to do anything else.`
            : ' You will keep trading throughout.'}
        </p>
      </Card>
    );
  }

  return (
    <Card
      className={cn('mb-4', licence.expired ? 'border-danger' : 'border-caution')}
      data-testid={`licence-renewal-${licence.sector}`}
    >
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        {licence.regulator}
        {multiple ? ` · ${licence.affects.replace('Your ', '')}` : null}
      </p>

      <h2 className="m-0 mt-1 font-display text-[15px] font-bold tracking-[-0.01em]">
        {licence.expired
          ? `${licence.affects} are paused`
          : `Your ${licence.label} expires in ${licence.days_remaining} ${licence.days_remaining === 1 ? 'day' : 'days'}`}
      </h2>

      <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
        {licence.expired
          ? `Your ${licence.label} expired on ${licence.expiry_date}. Send us the renewed one and we will put you back on sale — the rest of your shop is unaffected.`
          : 'Send us the renewed one now and nothing ever pauses. We check it by hand, usually within a working day.'}
      </p>

      {/* The reviewer's words, above the form. It is the thing they have to
          read before uploading the same photograph again. */}
      {licence.last_rejection ? (
        <p className="m-0 mt-3 border-t border-line pt-3 text-[13px] font-medium leading-relaxed text-danger">
          {licence.last_rejection}
        </p>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Row
          label={`${licence.regulator} number`}
          value={licenceNo}
          onChange={setLicenceNo}
          editable
          error={errors.licence_no?.[0]}
        />
        <Row
          label="New expiry date"
          value={expiry}
          onChange={setExpiry}
          editable
          type="date"
          error={errors.expiry_date?.[0]}
        />
      </div>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf"
        className="hidden"
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="grey" onClick={() => input.current?.click()}>
          {file ? 'Choose a different file' : 'Add the scan'}
        </Button>
        {file ? <span className="text-[12.5px] text-muted">{file.name}</span> : null}
        {errors.file?.[0] ? (
          <span role="alert" className="text-[12px] font-medium text-danger">
            {errors.file[0]}
          </span>
        ) : null}
      </div>

      {submit.error instanceof ApiError && !submit.error.isValidation ? (
        <p role="alert" className="m-0 mt-3 text-[13px] font-medium text-danger">
          {submit.error.message}
        </p>
      ) : null}

      <Button
        className="mt-4"
        disabled={!ready || submit.isPending}
        onClick={() =>
          submit.mutate({
            sector: licence.sector,
            licence_no: licenceNo.trim(),
            expiry_date: expiry,
            file: file!,
          })
        }
      >
        {submit.isPending ? 'Sending…' : 'Send it for checking'}
      </Button>

      <p className="m-0 mt-2 text-[12px] leading-relaxed text-muted">
        A photo taken on a phone is fine, as long as the number and the expiry date are both
        readable. We never put it on sale on your say-so — somebody here reads it first.
      </p>
    </Card>
  );
}

/** What is left. Shown as a list that visibly shrinks. */
function Checklist({ missing, ready }: { missing: string[]; ready: boolean }) {
  if (ready) {
    return (
      <Card className="mb-4 border-verified bg-verified-tint">
        <p className="m-0 flex items-center gap-2 text-[14px] font-bold text-verified-ink">
          <Icon name="check" size={17} />
          Everything is here
        </p>
        <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
          Send it to us and we will check your licence by hand.
        </p>
      </Card>
    );
  }

  return (
    <Card className="mb-4" data-testid="onboarding-checklist">
      <h2 className="m-0 mb-2 font-display text-[15px] font-bold tracking-[-0.01em]">
        Still needed ({missing.length})
      </h2>
      <ul className="m-0 list-none space-y-1.5 p-0">
        {missing.map((item) => (
          <li key={item} className="flex items-start gap-2 text-[13.5px] text-ink">
            <Icon name="x" size={14} className="mt-0.5 shrink-0 text-danger" />
            {item}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Documents({
  pharmacy,
  required,
  editable,
}: {
  pharmacy: SellerPharmacy;
  required: Record<string, string>;
  editable: boolean;
}) {
  return (
    <Card className="mb-4">
      <h2 className="m-0 mb-1 font-display text-[15px] font-bold tracking-[-0.01em]">Documents</h2>
      <p className="m-0 mb-3 text-[12.5px] leading-relaxed text-muted">
        Photos or PDFs. We read every one by hand — a clear picture taken on a phone is fine.
      </p>

      <div className="space-y-2.5">
        {Object.entries(required).map(([type, label]) => (
          <DocumentRow
            key={type}
            type={type}
            label={label}
            document={pharmacy.documents.find((doc) => doc.type === type)}
            editable={editable}
          />
        ))}
      </div>
    </Card>
  );
}

function DocumentRow({
  type,
  label,
  document,
  editable,
}: {
  type: string;
  label: string;
  document?: PharmacyDocumentSummary;
  editable: boolean;
}) {
  const upload = useUploadPharmacyDocument();
  const input = useRef<HTMLInputElement>(null);

  const rejected = document?.status === 'rejected';

  return (
    <div
      className={cn(
        'rounded-card border p-3',
        rejected ? 'border-danger bg-danger-tint' : 'border-line bg-white',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 text-[13.5px] font-semibold text-ink">{label}</p>
          {document ? (
            <p className="m-0 mt-0.5 text-[12px] text-muted">
              {document.status === 'approved'
                ? 'Checked and approved'
                : document.status === 'rejected'
                  ? 'Needs replacing'
                  : 'Uploaded — waiting to be checked'}
            </p>
          ) : (
            <p className="m-0 mt-0.5 text-[12px] text-muted">Not uploaded yet</p>
          )}
        </div>

        {document?.status === 'approved' ? (
          <Badge tone="verified">Approved</Badge>
        ) : editable ? (
          <>
            <input
              ref={input}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate({ type, file });
                event.target.value = '';
              }}
            />
            <Button
              size="sm"
              variant={document ? 'grey' : 'primary'}
              disabled={upload.isPending}
              onClick={() => input.current?.click()}
            >
              {upload.isPending ? 'Uploading…' : document ? 'Replace' : 'Upload'}
            </Button>
          </>
        ) : (
          <Badge tone="neutral">{document ? 'With us' : 'Missing'}</Badge>
        )}
      </div>

      {/* The reviewer's note is the whole value of a rejection. */}
      {document?.note ? (
        <p className="m-0 mt-2 text-[12.5px] font-medium leading-relaxed text-danger">
          {document.note}
        </p>
      ) : null}

      {upload.error ? (
        <p role="alert" className="m-0 mt-2 text-[12.5px] font-medium text-danger">
          {upload.error instanceof ApiError ? upload.error.message : 'That upload did not work.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Licence, superintendent and bank details — the fields approval depends on.
 *
 * ── WHICH LICENCE BLOCK APPEARS DEPENDS ON WHAT THEY SELL ───────────────────
 *
 * An agrovet store has no pharmacist and never will, so it is never shown a
 * field asking for one. A retail pharmacy that also stocks dog dewormer is
 * shown both, under two headings, because they are two registrations with two
 * expiry dates from two different councils — and folding them into one block
 * is how somebody renews the wrong one.
 */
function Details({ pharmacy, editable }: { pharmacy: SellerPharmacy; editable: boolean }) {
  const update = useUpdatePharmacy();
  const sellsHuman = pharmacy.sectors.includes('human');
  const sellsVet = pharmacy.sectors.includes('veterinary');

  const [form, setForm] = useState({
    pcn_licence_no: pharmacy.pcn_licence_no ?? '',
    pcn_licence_expiry: pharmacy.pcn_licence_expiry ?? '',
    superintendent_name: pharmacy.superintendent_name ?? '',
    vcn_licence_no: pharmacy.vcn_licence_no ?? '',
    vcn_licence_expiry: pharmacy.vcn_licence_expiry ?? '',
    vet_superintendent_name: pharmacy.vet_superintendent_name ?? '',
    phone: pharmacy.phone ?? '',
    rc_number: pharmacy.rc_number ?? '',
    bank_code: pharmacy.bank_code ?? '',
    account_number: '',
    account_name: pharmacy.account_name ?? '',
  });

  const errors = update.error instanceof ApiError ? update.error.fieldErrors : {};
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Card className="mb-4">
      <h2 className="m-0 mb-1 font-display text-[15px] font-bold tracking-[-0.01em]">
        Licence &amp; payout
      </h2>
      <p className="m-0 mb-3 text-[12.5px] leading-relaxed text-muted">
        Your bank details are where each sale lands. ChiCare never holds your money — Paystack
        splits it to you at the moment a buyer pays.
      </p>

      {sellsHuman ? (
        <LicenceBlock title="Human medicines — Pharmacists Council of Nigeria">
          <Row label="PCN licence number" value={form.pcn_licence_no} onChange={set('pcn_licence_no')} editable={editable} error={errors.pcn_licence_no?.[0]} />
          <Row label="Licence expires" value={form.pcn_licence_expiry} onChange={set('pcn_licence_expiry')} editable={editable} type="date" error={errors.pcn_licence_expiry?.[0]} />
          <Row label="Superintendent pharmacist" value={form.superintendent_name} onChange={set('superintendent_name')} editable={editable} />
        </LicenceBlock>
      ) : null}

      {sellsVet ? (
        <LicenceBlock title="Animal health — Veterinary Council of Nigeria" vet>
          <Row label="VCN registration number" value={form.vcn_licence_no} onChange={set('vcn_licence_no')} editable={editable} error={errors.vcn_licence_no?.[0]} />
          <Row label="Registration expires" value={form.vcn_licence_expiry} onChange={set('vcn_licence_expiry')} editable={editable} type="date" error={errors.vcn_licence_expiry?.[0]} />
          <Row label="Veterinary surgeon in charge" value={form.vet_superintendent_name} onChange={set('vet_superintendent_name')} editable={editable} />
        </LicenceBlock>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Row label="Business phone" value={form.phone} onChange={set('phone')} editable={editable} />
        <Row label="RC number" value={form.rc_number} onChange={set('rc_number')} editable={editable} />
        <Row label="Bank code" value={form.bank_code} onChange={set('bank_code')} editable={editable} />
        <Row
          label="Account number"
          value={form.account_number}
          onChange={set('account_number')}
          editable={editable}
          placeholder={pharmacy.account_number_last4 ? `•••• ${pharmacy.account_number_last4}` : '10 digits'}
          error={errors.account_number?.[0]}
        />
        <Row label="Account name" value={form.account_name} onChange={set('account_name')} editable={editable} />
      </div>

      {editable ? (
        <Button
          className="mt-4"
          disabled={update.isPending}
          onClick={() =>
            update.mutate(
              Object.fromEntries(Object.entries(form).filter(([, value]) => value !== '')),
            )
          }
        >
          {update.isPending ? 'Saving…' : update.isSuccess ? 'Saved' : 'Save details'}
        </Button>
      ) : null}
    </Card>
  );
}

/**
 * One regulator's block of fields.
 *
 * A heading naming the council, not just "Licence". Two registrations with two
 * expiry dates under one generic heading is how somebody renews the wrong one
 * and finds out when half their listings disappear.
 */
function LicenceBlock({
  title,
  vet = false,
  children,
}: {
  title: string;
  vet?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4">
      <p
        className={cn(
          'm-0 mb-2.5 border-b pb-1.5 font-mono text-[10px] uppercase tracking-[0.12em]',
          vet ? 'border-blue text-blue' : 'border-line text-faint',
        )}
      >
        {title}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  onChange,
  editable,
  type = 'text',
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  editable: boolean;
  type?: string;
  placeholder?: string;
  error?: string;
}) {
  if (!editable) {
    return (
      <div>
        <p className="m-0 text-[11px] uppercase tracking-[0.06em] text-faint">{label}</p>
        <p className="m-0 mt-0.5 text-[13.5px] text-ink">{value || '—'}</p>
      </div>
    );
  }

  return (
    <label className="block">
      <span className="mb-1.5 block text-[12.5px] font-medium text-ink">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'min-h-11 w-full rounded-btn border bg-paper px-3 text-[13.5px] text-ink outline-none focus:border-blue',
          error ? 'border-danger' : 'border-line',
        )}
      />
      {error ? (
        <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
          {error}
        </span>
      ) : null}
    </label>
  );
}

function SubmitPanel({ ready }: { ready: boolean }) {
  const submit = useSubmitPharmacy();

  return (
    <Card>
      {submit.error ? (
        <p role="alert" className="m-0 mb-2 text-[13px] font-medium text-danger">
          {submit.error instanceof ApiError ? submit.error.message : 'That did not go through.'}
        </p>
      ) : null}

      <Button size="lg" className="w-full" disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
        {submit.isPending ? 'Sending…' : 'Send for review'}
      </Button>

      <p className="m-0 mt-2 text-[12px] leading-relaxed text-muted">
        {ready
          ? 'You will not be able to edit while we are reviewing — we check the licence against what you sent, so it has to stay put.'
          : 'Fill in everything above first. The list at the top shows what is left.'}
      </p>
    </Card>
  );
}

function UnderReview() {
  return (
    <Card className="mb-4 border-blue bg-blue-tint">
      <p className="m-0 flex items-center gap-2 text-[14px] font-bold text-blue">
        <Icon name="clock" size={17} />
        With our team
      </p>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
        We check every licence against the PCN register by hand, so this usually takes a working
        day. We will message you either way — you do not need to chase us.
      </p>
    </Card>
  );
}

function Rejected({ documents }: { documents: PharmacyDocumentSummary[] }) {
  const notes = documents.filter((doc) => doc.note);

  return (
    <Card className="mb-4 border-danger bg-danger-tint">
      <p className="m-0 text-[14px] font-bold text-ink">We need a bit more before we can approve you</p>
      {notes.length > 0 ? (
        <ul className="m-0 mt-2 list-none space-y-1.5 p-0">
          {notes.map((doc) => (
            <li key={doc.type} className="text-[13px] leading-relaxed text-ink">
              <b>{doc.label}:</b> {doc.note}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="m-0 mt-2 text-[13px] leading-relaxed text-ink">
        Fix what is listed and send it back — there is no limit on how many times you can.
      </p>
    </Card>
  );
}

/**
 * Where this seller stands, in one card.
 *
 * ── WHY IT READS THE LICENCES RATHER THAN THE STATUS ────────────────────────
 *
 * Selling stops the moment a licence lapses, because `sellable` reads the
 * expiry date — but the STATUS only catches up when the nightly job runs. For
 * the hours in between, a seller with nothing on sale was being shown a green
 * card saying "You are live" directly beneath a panel telling them their
 * listings were paused.
 *
 * Two true statements that contradict each other are worse than either one
 * alone, so this card is derived from the same licences the panel above it
 * reads, and never from a status that can lag reality by a night.
 */
function Live({ licences }: { licences: SellerLicence[] }) {
  const expired = licences.filter((licence) => licence.expired);
  const live = licences.filter((licence) => !licence.expired);

  if (expired.length > 0) {
    return (
      <Card className="mb-4 border-line" data-testid="partly-paused">
        <p className="m-0 flex items-center gap-2 text-[14px] font-bold text-ink">
          <Icon name="alert" size={17} className="text-caution" />
          {live.length > 0 ? 'Part of your shop is paused' : 'Your shop is paused'}
        </p>
        <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
          {expired.map((licence) => licence.affects).join(' and ')} are off sale until we have a
          current {expired.map((licence) => licence.label).join(' and ')}.
          {live.length > 0
            ? ` ${live.map((licence) => licence.affects).join(' and ')} are trading normally.`
            : ''}{' '}
          Send the renewal above and your listings come back on their own.
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4 border-verified bg-verified-tint">
        <p className="m-0 flex items-center gap-2 text-[14px] font-bold text-verified-ink">
          <Icon name="check" size={17} />
          You are live
        </p>
        <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
          You are approved. Add products and they go on sale as soon as you enter a batch.
        </p>
        <Link
          to="/seller/listings"
          className="mt-3 inline-flex min-h-11 items-center rounded-btn bg-blue px-4 text-[13.5px] font-bold text-white no-underline"
        >
          Add your products
        </Link>
      </Card>

      {/* §10 Phase 1: thirty days out we warn, on expiry we auto-suspend. The
          first a seller hears of it must not be their listings vanishing.

          Two separate warnings, never one. They lapse independently and take
          down different halves of the shop, so "your licence expires soon" on a
          seller holding two of them is a sentence that cannot be acted on. */}
      {licences.map((licence) => (
        <LicenceWarning key={licence.sector} licence={licence} />
      ))}
    </>
  );
}

/**
 * The countdown, while there is still something to count down to.
 *
 * Renders nothing once a licence has actually lapsed: the renewal panel above
 * owns that case, and a second card reading "expires in -1 days" is both wrong
 * and a worse version of what is already on screen.
 */
function LicenceWarning({ licence }: { licence: SellerLicence }) {
  const days = licence.days_remaining;

  if (licence.expired || typeof days !== 'number' || days < 0 || days > 30) {
    return null;
  }

  return (
    <Card
      className="mb-4 border-caution bg-caution-tint"
      data-testid={`licence-warning-${licence.sector}`}
    >
      <p className="m-0 text-[13.5px] font-bold text-ink">
        Your {licence.label} expires in {days} day{days === 1 ? '' : 's'}
      </p>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-ink">
        Send us the renewal before then. The day it lapses we pause{' '}
        {licence.affects.replace('Your ', 'your ')} automatically — it is the one thing we cannot
        make an exception for. The rest of your shop keeps trading.
      </p>
    </Card>
  );
}

function StatusBadge({ status, paused = false }: { status: string; paused?: boolean }) {
  if (paused) {
    return <Badge tone="caution">Paused</Badge>;
  }

  const map: Record<string, { tone: 'neutral' | 'brand' | 'verified' | 'caution' | 'danger'; label: string }> = {
    draft: { tone: 'neutral', label: 'Draft' },
    pending_approval: { tone: 'brand', label: 'Under review' },
    active: { tone: 'verified', label: 'Live' },
    suspended: { tone: 'danger', label: 'Paused' },
    rejected: { tone: 'caution', label: 'Needs changes' },
  };

  const { tone, label } = map[status] ?? { tone: 'neutral' as const, label: status };

  return <Badge tone={tone}>{label}</Badge>;
}
